import React from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin, Phone, ArrowRight, Sparkles, Award, Tv, Star, BookOpen, GraduationCap,
  Globe2, Mail, Instagram, Youtube, Facebook, Users, CheckCircle2, Heart, ShieldCheck
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEO from '../components/SEO';
import {
  personSchema, organizationSchema, websiteSchema, localBusinessSchema,
  VIVEK_ID, VIVEK_URL, VIVEK_PHOTO, GOOGLE_BUSINESS_URL,
} from '../utils/schemas';
import { trackWhatsAppClick, trackConsultationCTAClick, trackCallClick, trackEvent } from '../utils/analytics';

const HERO = '/media/founder/IMG_9620-removebg-preview.png';
const WHATSAPP = '+919311369729';
const WA_HREF = `https://wa.me/${WHATSAPP.replace('+','')}?text=${encodeURIComponent('Namaste Acharya ji, I would like to book a consultation.')}`;

const WHY_CHOOSE = [
  { icon: BookOpen,    title: 'Shloka-backed Predictions',    desc: 'Every reading is rooted in Brihat Parashar Hora Shastra, Phaldeepika and Saravali — never guesswork, never fear-driven.' },
  { icon: ShieldCheck, title: 'Strict Privacy & Ethics',      desc: 'Your birth details and conversations are 100% private. No data sharing, no upsell pressure.' },
  { icon: Heart,       title: 'Remedy-Focused Guidance',      desc: 'Practical Vedic remedies — mantra, rudraksha, gemstone, daan — tailored to your specific dasha and grah dosha.' },
  { icon: CheckCircle2,title: 'Honest Counsel, Not Fear',     desc: 'Acharya जी never sells fear. If your chart needs no remedy, you will be told so — and saved your money.' },
  { icon: Globe2,      title: '14+ Countries · 33,000+ Clients', desc: 'Trusted by NRIs in Dubai (since 2001), USA, UK, Canada, Singapore, Australia — across cultures and time zones.' },
  { icon: Users,       title: '33,300+ YouTube Subscribers',  desc: 'Free weekly Vedic education on YouTube — a community of seekers who learned to read their own kundli.' },
];

const HIGHLIGHTS = [
  { label: 'Years of Practice',          value: '33+' },
  { label: 'Clients Consulted',          value: '33,000+' },
  { label: 'Countries Served',           value: '14+' },
  { label: 'Dubai NRI Clients Since',    value: '2001' },
  { label: 'YouTube Subscribers',        value: '33,300+' },
  { label: 'Google Verified Rating',     value: '4.9 ★' },
];

const MEDIA = [
  { name: 'Zee News',    tone: 'from-red-600 to-red-500' },
  { name: 'Aaj Tak',     tone: 'from-amber-600 to-yellow-500' },
  { name: 'ABP News',    tone: 'from-rose-700 to-red-600' },
  { name: 'News18',      tone: 'from-blue-700 to-sky-600' },
  { name: 'News Nation', tone: 'from-emerald-700 to-teal-600' },
];

const CREDENTIALS = [
  { icon: Award,         title: '33+ years of Vedic practice',                desc: 'Decades of one-to-one Kundli analysis, Parashari Jyotish, Vastu and remedy guidance.' },
  { icon: Tv,            title: 'Featured on National Media',                 desc: 'Zee News, Aaj Tak, ABP News, News18 and News Nation prime-time astrology programmes.' },
  { icon: GraduationCap, title: 'Authority in Parashari Jyotish',             desc: 'Deep mastery of classical texts — Brihat Parashar Hora Shastra, Phaldeepika, Saravali.' },
  { icon: BookOpen,      title: 'Vastu Shastra Consultant',                   desc: 'Direction-wise (9-graha) Vastu remediation for homes, offices and commercial spaces.' },
  { icon: Globe2,        title: '33,000+ clients · 14+ countries',            desc: 'Consulted clients across India, UAE, USA, UK, Canada, Singapore, Australia and more.' },
  { icon: Star,          title: 'BJP National Spokesperson (former)',         desc: 'Public-life experience that informs his counsel for politicians, executives and families alike.' },
];

const FAQ = [
  {
    q: 'Who is Vivek Virat Mudgal?',
    a: 'Vivek Virat Mudgal is the Founder of ViratAstro — a Vedic Astrologer, Vastu Consultant and Parashari Jyotish expert with 33+ years of professional experience. He has been featured on News Nation and has consulted with 33,000+ clients across 14+ countries.',
  },
  {
    q: 'What services does Vivek Virat Mudgal offer?',
    a: 'Complete Vedic kundli analysis, Parashari Jyotish predictions, Vastu Shastra consultation, Gun Milan (marriage compatibility), Numerology, gemstone recommendation, muhurat, and personalised spiritual remedies.',
  },
  {
    q: 'How can I book a consultation with Vivek जी?',
    a: 'You can WhatsApp +91 9311369729, use the Pucho Guruji AI consultation, or fill the consultation form on the contact page. Personal consultations are confirmed within 24 hours.',
  },
  {
    q: 'Where is Vivek Mudgal based?',
    a: 'Faridabad, Haryana, India. He offers in-person consultations in Delhi NCR and online consultations worldwide.',
  },
  {
    q: 'What makes Vivek Mudgal different from other astrologers?',
    a: 'Strict adherence to classical Parashari Jyotish (not modern hybrid astrology), 33+ years of practice, traceable shloka-backed predictions, and a focus on practical remedies and ethical counsel rather than fear-based fortune-telling.',
  },
];

function CredCard({ icon: Icon, title, desc }) {
  return (
    <div className="group rounded-2xl bg-bg-card border border-border-gold/30 p-6 hover:border-gold/60 transition-all" data-testid={`vivek-cred-${title.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'').slice(0,30)}`}>
      <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-gold" />
      </div>
      <h3 className="text-lg font-bold text-text-primary mb-2">{title}</h3>
      <p className="text-sm text-text-muted leading-relaxed">{desc}</p>
    </div>
  );
}

export default function VivekMudgalPage() {
  const schemas = [
    personSchema(),
    organizationSchema(),
    websiteSchema(),
    localBusinessSchema(),
    {
      '@context': 'https://schema.org',
      '@type': 'ProfilePage',
      mainEntity: { '@id': VIVEK_ID },
      url: VIVEK_URL,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.map(({ q, a }) => ({
        '@type': 'Question', name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary">
      <SEO
        title="Vivek Virat Mudgal — Founder of ViratAstro · Vedic Astrologer · Parashari Jyotish Expert"
        description="Vivek Virat Mudgal is the Founder of ViratAstro with 33+ years of experience in Vedic Astrology, Parashari Jyotish, Vastu Shastra, and Spiritual Guidance. Featured on News Nation. Consulted 33,000+ clients across 14+ countries."
        path="/experts/vivek-virat-mudgal"
        image={VIVEK_PHOTO}
        type="profile"
        keywords="Vivek Virat Mudgal, Acharya Vivek Mudgal, Vedic Astrologer, Parashari Jyotish Expert, Vastu Consultant, ViratAstro Founder, Faridabad Astrologer, Best Vedic Astrologer India, Kundli Expert, Spiritual Guide"
        jsonLd={schemas}
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Experts', path: '/experts' },
          { name: 'Vivek Virat Mudgal', path: '/experts/vivek-virat-mudgal' },
        ]}
      />

      <Header />

      {/* HERO */}
      <section className="relative pt-28 pb-16 overflow-hidden" data-testid="vivek-hero">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_30%_20%,rgba(255,215,0,0.18),transparent_60%),radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.12),transparent_60%)]" />
        <div className="container mx-auto px-4 max-w-7xl relative">
          <div className="text-center mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gold text-bg-primary text-[11px] tracking-[0.3em] uppercase font-bold">
              ⭐ FOUNDER · ViratAstro
            </span>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold leading-tight mb-4">
                Vivek Virat Mudgal
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-gold/90 font-light leading-snug mb-5">
                Vedic Astrologer · Vastu Consultant · Parashari Jyotish Expert
              </p>
              <p className="text-sm sm:text-base text-text-muted leading-relaxed mb-5">
                Founder of <b className="text-text-primary">ViratAstro</b> with <b className="text-text-primary">33+ years</b> of dedicated practice in <b className="text-text-primary">Vedic Astrology, Parashari Jyotish, Vastu Shastra and Spiritual Guidance</b>. Featured on <b className="text-text-primary">Zee News, Aaj Tak, ABP News &amp; News18</b>. Trusted by <b className="text-text-primary">33,000+ clients across 14+ countries</b>, including <b className="text-text-primary">Dubai NRIs since 2001</b>.
              </p>
              <p className="text-sm text-text-muted leading-relaxed mb-7">
                Acharya Vivek जी's approach is rooted in classical Sanskrit texts — Brihat Parashar Hora Shastra, Phaldeepika, Saravali — combined with three decades of practical case experience. His predictions are <b className="text-text-primary">shloka-backed, ethical and remedy-focused</b>, never fear-driven.
              </p>

              <div className="flex flex-wrap gap-3">
                <a
                  href={WA_HREF}
                  target="_blank" rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick('vivek_profile_hero')}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-green-600 text-white font-semibold text-sm hover:bg-green-700 transition shadow-lg shadow-green-900/30"
                  data-testid="vivek-whatsapp-cta"
                >
                  <Phone className="w-4 h-4" /> WhatsApp +91 9311369729
                </a>
                <a
                  href="tel:+919311369729"
                  onClick={() => trackCallClick('vivek_profile_hero')}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-purple-700 text-white font-semibold text-sm hover:bg-purple-800 transition shadow-lg shadow-purple-900/30"
                  data-testid="vivek-call-cta"
                >
                  <Phone className="w-4 h-4" /> Call Now
                </a>
                <Link
                  to="/poocho-guruji"
                  onClick={() => trackConsultationCTAClick('vivek_profile_hero_poocho', { value: 99 })}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-bg-primary font-semibold text-sm hover:bg-yellow-400 transition"
                  data-testid="vivek-poocho-cta"
                >
                  Pucho Guruji — व्यक्तिगत परामर्श <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="flex flex-wrap gap-5 mt-7 text-xs text-text-muted">
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-gold" /> Faridabad, Haryana · India</span>
                <span className="flex items-center gap-1"><Mail className="w-4 h-4 text-gold" /> vivek.mudgal.vm@gmail.com</span>
              </div>

              <div className="flex gap-3 mt-4">
                <a href="https://www.instagram.com/astrovivekmudgal" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-pink-500/15 flex items-center justify-center text-pink-400 hover:text-pink-300 transition" aria-label="Instagram"><Instagram className="w-4 h-4" /></a>
                <a href="https://youtube.com/@astrovivekmudgal" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-red-500/15 flex items-center justify-center text-red-400 hover:text-red-300 transition" aria-label="YouTube"><Youtube className="w-4 h-4" /></a>
                <a href="https://www.facebook.com/share/1KEWbgnpEE/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-blue-500/15 flex items-center justify-center text-blue-400 hover:text-blue-300 transition" aria-label="Facebook"><Facebook className="w-4 h-4" /></a>
                <a href={GOOGLE_BUSINESS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold transition" aria-label="Leave a Google Review" data-testid="vivek-google-review">
                  <Star className="w-3.5 h-3.5 fill-current" /> Google Review
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-gold/30 via-purple-500/15 to-pink-500/10 blur-3xl rounded-[3rem]" />
              <img
                src={HERO}
                alt="Vivek Virat Mudgal — Founder of ViratAstro, Vedic Astrologer and Parashari Jyotish Expert"
                className="relative w-full rounded-[2rem] border border-gold/40 shadow-2xl bg-gradient-to-br from-bg-card to-bg-secondary p-8"
                loading="eager"
                data-testid="vivek-hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS STRIP — Quick scannable proof points */}
      <section className="relative py-8 border-y border-gold/20 bg-gradient-to-r from-[#0f0508] via-[#15093A] to-[#0f0508]" data-testid="vivek-highlights">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 sm:gap-6">
            {HIGHLIGHTS.map((h, i) => (
              <div key={i} className="text-center" data-testid={`vivek-highlight-${i}`}>
                <p className="font-heading text-2xl sm:text-3xl text-gold-gradient leading-tight">{h.value}</p>
                <p className="text-[10px] sm:text-xs text-text-muted mt-1 leading-tight uppercase tracking-wider">{h.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEDIA STRIP — As Seen On */}
      <section className="py-10 bg-bg-primary" data-testid="vivek-media-strip">
        <div className="container mx-auto px-4 max-w-7xl">
          <p className="text-center text-[11px] sm:text-xs tracking-[0.3em] uppercase text-gold/80 font-semibold mb-5">
            <Tv className="inline w-3.5 h-3.5 mr-2 -mt-0.5" />
            As Featured On National Media
          </p>
          <div className="flex flex-wrap justify-center items-center gap-2 sm:gap-3">
            {MEDIA.map((m) => (
              <span
                key={m.name}
                className={`inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r ${m.tone} text-white text-[11px] sm:text-sm font-bold tracking-wide shadow-md ring-1 ring-white/10`}
                data-testid={`vivek-media-${m.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {m.name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE — strong differentiator block */}
      <section className="py-14 bg-bg-secondary" data-testid="vivek-why-choose">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-10">
            <span className="text-xs tracking-[0.3em] text-gold/70 uppercase">Why Choose Acharya Vivek जी</span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mt-2">
              Not just an astrologer — <span className="text-gold-gradient">your trusted Vedic guide</span>
            </h2>
            <p className="text-sm sm:text-base text-text-muted mt-3 max-w-2xl mx-auto">
              33+ years of dedicated practice. Featured on Zee News, Aaj Tak, ABP News, News18. Trusted by Dubai NRIs since 2001.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CHOOSE.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.title}
                  className="group rounded-2xl bg-bg-card border border-gold/20 p-6 hover:border-gold/60 transition-all"
                  data-testid={`vivek-why-${c.title.toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9-]/g,'').slice(0,30)}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center mb-4 group-hover:bg-gold/20 transition">
                    <Icon className="w-6 h-6 text-gold" />
                  </div>
                  <h3 className="text-lg font-bold text-text-primary mb-2">{c.title}</h3>
                  <p className="text-sm text-text-muted leading-relaxed">{c.desc}</p>
                </div>
              );
            })}
          </div>

          {/* Inline CTA inside Why Choose */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={WA_HREF}
              target="_blank" rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('vivek_profile_why_choose')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-green-600 text-white font-semibold text-sm hover:bg-green-700 transition shadow-lg shadow-green-900/30"
              data-testid="vivek-why-whatsapp-cta"
            >
              <Phone className="w-4 h-4" /> Chat with Acharya जी on WhatsApp
            </a>
            <Link
              to="/poocho-guruji"
              onClick={() => trackConsultationCTAClick('vivek_profile_why_choose_poocho', { value: 99 })}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-bg-primary font-semibold text-sm hover:bg-yellow-400 transition"
              data-testid="vivek-why-poocho-cta"
            >
              Ask AI Guruji · ₹99 <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CREDENTIALS */}
      <section className="py-14 bg-bg-secondary" data-testid="vivek-credentials">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-10">
            <span className="text-xs tracking-[0.3em] text-gold/70 uppercase">Credentials &amp; Authority</span>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold mt-2">Three decades of Vedic mastery</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CREDENTIALS.map((c) => <CredCard key={c.title} {...c} />)}
          </div>
        </div>
      </section>

      {/* SERVICES BRIDGE */}
      <section className="py-14" data-testid="vivek-services">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="rounded-3xl bg-gradient-to-br from-bg-card to-bg-secondary border border-gold/20 p-8 sm:p-10 text-center">
            <span className="text-xs tracking-[0.3em] text-gold/70 uppercase">Consult with Acharya Vivek जी</span>
            <h2 className="text-2xl sm:text-3xl font-heading font-bold mt-2 mb-5">Choose your consultation path</h2>
            <div className="grid sm:grid-cols-3 gap-3 mt-6">
              <Link to="/kundli" className="rounded-2xl bg-bg-primary border border-gold/30 hover:border-gold p-5 transition group" data-testid="link-kundli">
                <Sparkles className="w-6 h-6 text-gold mb-2 group-hover:scale-110 transition" />
                <h3 className="font-bold mb-1">Free Kundli</h3>
                <p className="text-xs text-text-muted">Swiss Ephemeris accurate</p>
              </Link>
              <Link to="/poocho-guruji" className="rounded-2xl bg-bg-primary border border-gold/30 hover:border-gold p-5 transition group" data-testid="link-poocho">
                <Star className="w-6 h-6 text-gold mb-2 group-hover:scale-110 transition" />
                <h3 className="font-bold mb-1">Pucho Guruji</h3>
                <p className="text-xs text-text-muted">AI + Acharya analysis</p>
              </Link>
              <Link to="/upay" className="rounded-2xl bg-bg-primary border border-gold/30 hover:border-gold p-5 transition group" data-testid="link-upay">
                <Award className="w-6 h-6 text-gold mb-2 group-hover:scale-110 transition" />
                <h3 className="font-bold mb-1">Vedic Upay</h3>
                <p className="text-xs text-text-muted">9-graha remedies</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-bg-secondary" data-testid="vivek-faq">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-8">
            <span className="text-xs tracking-[0.3em] text-gold/70 uppercase">FAQ</span>
            <h2 className="text-3xl font-heading font-bold mt-2">About Acharya Vivek जी</h2>
          </div>
          <div className="space-y-3">
            {FAQ.map(({ q, a }, i) => (
              <details key={i} className="group rounded-xl bg-bg-card border border-border-gold/20 p-5 hover:border-gold/40 transition" data-testid={`vivek-faq-${i}`}>
                <summary className="font-semibold text-text-primary cursor-pointer list-none flex justify-between items-center">
                  {q}
                  <span className="text-gold text-xl transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm text-text-muted leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA BAND — Last chance conversion before user leaves the page */}
      <section className="py-14 relative overflow-hidden" data-testid="vivek-bottom-cta">
        <div className="absolute inset-0 bg-gradient-to-br from-[#15093A] via-[#1f0d52] to-[#0f0508]" />
        <div className="absolute -top-24 left-1/4 w-96 h-96 bg-gold/10 rounded-full blur-[140px]" />
        <div className="absolute -bottom-24 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-[140px]" />
        <div className="container mx-auto px-4 max-w-4xl relative z-10 text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-gold/80 font-semibold mb-3">
            Ready to talk with Acharya जी?
          </p>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold mb-4">
            One conversation can <span className="text-gold-gradient">change everything</span>
          </h2>
          <p className="text-sm sm:text-base text-text-muted max-w-2xl mx-auto mb-8">
            33+ years of Vedic experience are just one message away. WhatsApp Acharya जी directly, call now,
            or try Pucho Guruji AI consultation for ₹99 — get a shloka-backed Vedic answer in minutes.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={WA_HREF}
              target="_blank" rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('vivek_profile_bottom')}
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-green-600 text-white font-semibold text-sm hover:bg-green-700 transition shadow-lg shadow-green-900/40"
              data-testid="vivek-bottom-whatsapp-cta"
            >
              <Phone className="w-4 h-4" /> WhatsApp Acharya जी
            </a>
            <a
              href="tel:+919311369729"
              onClick={() => trackCallClick('vivek_profile_bottom')}
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-purple-700 text-white font-semibold text-sm hover:bg-purple-800 transition shadow-lg shadow-purple-900/40"
              data-testid="vivek-bottom-call-cta"
            >
              <Phone className="w-4 h-4" /> Call +91 93113 69729
            </a>
            <Link
              to="/poocho-guruji"
              onClick={() => trackConsultationCTAClick('vivek_profile_bottom_poocho', { value: 99 })}
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-gold text-bg-primary font-semibold text-sm hover:bg-yellow-400 transition shadow-lg"
              data-testid="vivek-bottom-poocho-cta"
            >
              Pucho Guruji · ₹99 <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <p className="text-[11px] text-text-muted/60 mt-6">
            🔒 100% private · 🪔 33+ years of Vedic practice · 🌍 Trusted across 14+ countries
          </p>
        </div>
      </section>

      {/* INTERNAL LINKING */}
      <section className="py-10">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <p className="text-xs tracking-[0.3em] text-gold/70 uppercase mb-3">Explore</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/blog" className="px-5 py-2 rounded-full border border-gold/30 text-sm text-gold hover:bg-gold/10 transition">Articles by Vivek जी →</Link>
            <Link to="/experts/priya-khurana" className="px-5 py-2 rounded-full border border-gold/30 text-sm text-gold hover:bg-gold/10 transition">Featured Expert — Priya Khurana</Link>
            <Link to="/about" className="px-5 py-2 rounded-full border border-gold/30 text-sm text-gold hover:bg-gold/10 transition">About ViratAstro</Link>
            <Link to="/" className="px-5 py-2 rounded-full border border-gold/30 text-sm text-gold hover:bg-gold/10 transition">Home</Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </div>
  );
}
